import { type MouseEvent, memo, useMemo, useCallback, useEffect } from "react";

import { useProduct } from "@shopify/hydrogen-react";
import {
  type MediaImage,
  type ProductOptionValue,
} from "@shopify/hydrogen-react/storefront-api-types";

import { Slide } from "@components/Carousel";
import { useCarouselVisibility } from "@components/Carousel/CarouselVisibilityContext";
import { type ColorSwatchSize, ColorSwatch } from "@components/ColorSwatch";
import type { WithBreakpoints } from "@react/utilities";
import { handleize, isColorOption } from "@react/utilities";
import { handleKeyboardActivation } from "@react/utilities/accessibility";
import { SelectedVariantAvailability } from "@root/tablescape/constants";
import { formatProduct, formatProductVariant } from "@tablescape/utilities";
import { tw } from "classnames";

type Props = {
  index: number;
  onMouseEnter?: () => void;
  optionName: string;
  optionValue: ProductOptionValue;
  swatchSize?: WithBreakpoints<ColorSwatchSize>;
};

const styles = {
  slide: (hasOptionLabel: boolean) =>
    tw("flex", "items-center", {
      "pb-1": hasOptionLabel,
    }),
};

const DEFAULT_SWATCH_SIZE = {
  sm: "tiny" as const,
  md: "mini" as const,
};

const ProductCardOptionBase = ({
  index,
  onMouseEnter,
  optionName,
  optionValue,
  swatchSize = DEFAULT_SWATCH_SIZE,
}: Props) => {
  const { product, selectedOptions, setSelectedOption, variants } =
    useProduct();
  const { isVisible } = useCarouselVisibility();

  // Focus management: blur the swatch if it becomes invisible while focused
  useEffect(() => {
    const isCurrentlyVisible = isVisible(index);

    if (!isCurrentlyVisible) {
      // Find the slide by its data-carousel-key and index
      const carousel = document.querySelector(`[data-carousel-key]`);
      if (!carousel) return;

      const slides = carousel.querySelectorAll(
        ".splide__slide:not([data-arrow])"
      );
      const slide = slides[index] as HTMLElement;
      if (!slide) return;

      // Find the ColorSwatch within this slide
      const swatch = slide.querySelector<HTMLElement>('[role="radio"]');
      if (swatch && document.activeElement === swatch) {
        // Move focus to next logical element before blurring
        const nextFocusable = slide.nextElementSibling;
        if (
          nextFocusable &&
          nextFocusable instanceof HTMLElement &&
          nextFocusable.tabIndex >= 0
        ) {
          nextFocusable.focus();
        } else {
          // Fallback: find next focusable element in the carousel
          const focusableElements = carousel.querySelectorAll(
            '[tabindex]:not([tabindex="-1"])'
          );
          if (focusableElements) {
            const focusableArray = Array.from(focusableElements).filter(
              (element): element is HTMLElement =>
                element instanceof HTMLElement
            );
            const currentIndex = focusableArray.indexOf(swatch);
            const nextElement = focusableArray[currentIndex + 1];
            if (nextElement) {
              nextElement.focus();
            } else {
              swatch.blur();
            }
          } else {
            swatch.blur();
          }
        }
      }
    }
  }, [isVisible, index]);

  const formattedProduct = useMemo(() => formatProduct(product), [product]);

  const colorOption = useMemo(
    () => formattedProduct.options?.find((option) => isColorOption(option)),
    [formattedProduct.options]
  );

  const getVariantFromOptionValue = useCallback(
    (value: string) => {
      if (!colorOption) {
        return;
      }

      const foundVariant = variants.find((variant) =>
        variant.selectedOptions.some(
          (option) => option.name === colorOption.name && option.value === value
        )
      );

      if (foundVariant) {
        return formatProductVariant(foundVariant);
      }
    },
    [variants, colorOption]
  );

  const variant = useMemo(
    () => getVariantFromOptionValue(optionValue.name),
    [getVariantFromOptionValue, optionValue.name]
  );

  const handleSwatchMouseEnter = useCallback(
    (name: string, value: string) => () => {
      setSelectedOption(name, value);
      if (onMouseEnter) onMouseEnter();
    },
    [setSelectedOption, onMouseEnter]
  );

  const handleSwatchKeyDown = useMemo(
    () =>
      handleKeyboardActivation(() =>
        setSelectedOption(optionName, optionValue.name)
      ),
    [optionName, optionValue.name, setSelectedOption]
  );

  const isVariantAvailable = useMemo(
    () =>
      variant?.availableForSale &&
      (variant?.shippingNote
        ? handleize(variant.shippingNote) !==
          SelectedVariantAvailability.SOLD_OUT
        : true),
    [variant?.availableForSale, variant?.shippingNote]
  );

  const isVariantBackordered = useMemo(
    () =>
      !variant?.disableWaitlist &&
      (variant?.shippingNote
        ? handleize(variant.shippingNote) !==
          SelectedVariantAvailability.SOLD_OUT
        : false),
    [variant?.disableWaitlist, variant?.shippingNote]
  );

  const isVariantActive = useMemo(
    () => selectedOptions[optionName] === optionValue.name,
    [optionName, optionValue.name, selectedOptions]
  );

  const handleSwatchClick = useCallback(
    (e?: MouseEvent<HTMLDivElement>) => {
      e?.preventDefault();
      e?.stopPropagation();
      setSelectedOption(optionName, optionValue.name);
    },
    [optionName, optionValue.name, setSelectedOption]
  );

  if (
    !variant ||
    variant.hidden ||
    !(optionValue.swatch?.color || optionValue.swatch?.image)
  ) {
    return null;
  }

  return (
    <Slide
      className={styles.slide(!!variant.optionLabel)}
      onMouseEnter={handleSwatchMouseEnter(optionName, optionValue.name)}
    >
      <ColorSwatch
        active={isVariantActive}
        alt={variant.title || undefined}
        ariaDescribedBy={
          variant.optionLabel ? `${variant.id}-badge` : undefined
        }
        ariaHidden={!isVisible(index)}
        ariaLabel={`Select ${optionValue.name} color`}
        available={isVariantAvailable}
        backordered={isVariantBackordered}
        checked={isVariantActive}
        hex={optionValue.swatch.color}
        image={(optionValue.swatch.image as MediaImage)?.image?.url}
        onClick={handleSwatchClick}
        onKeyDown={handleSwatchKeyDown}
        optionLabel={variant.optionLabel}
        role="radio"
        size={swatchSize}
        tabIndex={isVisible(index) ? 0 : -1}
      />
    </Slide>
  );
};

export const ProductCardOption = memo(ProductCardOptionBase);
